/*
Copyright (C) 2024 Laboratorio Visao Robotica e Imagem

Departamento de Informatica - Universidade Federal do Parana - VRI/UFPR

This file is part of CienciaNaEscola. CienciaNaEscola is free software: you can redistribute it and/or modify it under the terms of the GNU
General Public License as published by the Free Software Foundation, either version 3 of the License, or (at your option) any later version.
CienciaNaEscola is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU General Public License for more details. You should have received a copy
of the GNU General Public License along with CienciaNaEscola.  If not, see <https://www.gnu.org/licenses/>
*/

import axios from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import CustomContainer from '../components/CustomContainer';
import NavBar from '../components/Navbar';
import { AuthContext } from '../contexts/AuthContext';
import SplashPage from './SplashPage';
import ErrorPage from './ErrorPage';
// import TextButton from '../components/TextButton';
import RoundedButton from '../components/RoundedButton';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import Gallery from '../components/Gallery';
import GalleryModal from '../components/GalleryModal';
import Chart from 'react-google-charts';
import TableInput from '../components/inputs/answers/TableInput';
import { AlertContext } from '../contexts/AlertContext';
import MaterialSymbol from '../components/MaterialSymbol';

const styles = `
    .bg-yellow-orange {
        background-color: #FECF86;
    }

    .color-yellow-orange {
        color: #FECF86;
    }

    .bg-light-gray {
        background-color: #D9D9D9;
    }

    .bg-coral-red {
        background-color: #F59489;
    }

    .color-gray {
        color: #787878 !important;
    }

    .font-century-gothic {
        font-family: 'Century Gothic', sans-serif;
    }

    .color-dark-gray {
        color: #535353 !important;
    }

    .font-barlow {
        font-family: 'Barlow', sans-serif;
    }

    @media (min-width: 992px) {
      .position-lg-sticky {
        position: sticky !important;
        top: 0;
      }
    }
`;

function ProtocolAnswersPage(props) {
    const { protocolId } = useParams();
    const { user } = useContext(AuthContext);
    const [protocolWAnswers, setProtocolWAnswers] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(undefined);
    const mapRef = useRef();
    const galleryModalRef = useRef();
    const [selectedAnswer, setSelectedAnswer] = useState(undefined);
    const [selectedItem, setSelectedItem] = useState(undefined);
    const [selectedApplication, setSelectedApplication] = useState(undefined);
    const { showAlert } = useContext(AlertContext);

    const setVisualization = (person, question, application) => {
        setSelectedAnswer(person);
        setSelectedItem(question);
        setSelectedApplication(application);
    };

    useEffect(() => {
        if (isLoading && user.status !== 'loading') {
            axios
                .get(`${process.env.REACT_APP_API_URL}api/protocol/getProtocolWithAnswers/${protocolId}`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                })
                .then((response) => {
                    setProtocolWAnswers(response.data.data);
                    setIsLoading(false);
                })
                .catch((error) =>
                    setError({ text: 'Erro ao obter respostas do protocolo', description: error.response?.data.message || '' })
                );
        }
    }, [protocolId, user.token, user.status, isLoading]);

    /**
     * Função para alternar o status de aprovação da resposta
     * Corresponde ao controller toggleApplicationAnswerApproval (API)
     * @param {string} applicationAnswerId - ID da resposta da aplicação a ser alterada
     * @param {string} targetStatus - Status de aprovação desejado ('APPROVED' ou 'REJECTED')
     */
    const toggleApprovalStatus = (applicationAnswerId, targetStatus) => {
        axios
            .put(
                `${process.env.REACT_APP_API_URL}api/applicationAnswer/toggleApplicationAnswerApproval/${applicationAnswerId}`,
                { status: targetStatus },
                { headers: { Authorization: `Bearer ${user.token}` } }
            )
            .then((response) => {
                showAlert({
                    headerText: targetStatus === 'APPROVED' ? 'Resposta aprovada com sucesso' : 'Resposta desaprovada com sucesso',
                    message: response.data.message,
                });
                setProtocolWAnswers((prev) => {
                    const newProtocol = { ...prev };
                    newProtocol.applications = newProtocol.applications.map((application) => {
                        const newApplication = { ...application };
                        newApplication.answers = newApplication.answers.map((answer) => {
                            const newAnswer = { ...answer };
                            if (newAnswer.id === applicationAnswerId) {
                                newAnswer.approvalStatus = targetStatus;
                            }
                            return newAnswer;
                        });
                        return newApplication;
                    });
                    return newProtocol;
                });
            })
            .catch((error) =>
                showAlert({
                    headerText: 'Erro ao alterar status da resposta',
                    message: error.response?.data.message || '',
                })
            );
    };

    /**
     * Função para alternar a habilitação (Ativa/Inativa)
     * Corresponde ao controller toggleApplicationAnswerEnablement (API)
     * @param {string} applicationAnswerId - ID da resposta da aplicação a ser alterada
     */
    const toggleEnablement = async (applicationAnswerId) => {
        axios
            .put(
                `${process.env.REACT_APP_API_URL}api/applicationAnswer/toggleApplicationAnswerEnablement/${applicationAnswerId}`,
                { enabled: false },
                { headers: { Authorization: `Bearer ${user.token}` } }
            )
            .then((response) => {
                showAlert({
                    headerText: 'Resposta desativada com sucesso',
                    message: response.data.message,
                });
                setProtocolWAnswers((prev) => {
                    const newProtocol = { ...prev };
                    newProtocol.applications = newProtocol.applications.map((application) => {
                        const newApplication = { ...application };
                        newApplication.answers = newApplication.answers.map((answer) => {
                            const newAnswer = { ...answer };
                            if (newAnswer.id === applicationAnswerId) {
                                newAnswer.enabled = false;
                            }
                            return newAnswer;
                        });
                        return newApplication;
                    });
                    return newProtocol;
                });
            })
            .catch((error) =>
                showAlert({
                    headerText: 'Erro ao alterar status da resposta',
                    message: error.response?.data.message || '',
                })
            );
    };

    /**
     * Retorna as configurações de exibição (rótulo, cores e ícone) com base no status de aprovação da resposta.
     * @param {string} approvalStatus - Status de aprovação da resposta ('APPROVED', 'REJECTED' ou 'PENDING')
     * @returns {Object} Configurações de exibição para o status fornecido
     */
    const getStatusConfig = (approvalStatus) => {
        if (approvalStatus === 'APPROVED') {
            return {
                label: 'Aprovada',
                colorHsl: [97, 43, 70],
                textColor: '#4e7d44',
                bgColor: '#e2f3dd',
                icon: 'check_circle',
            };
        } else if (approvalStatus === 'REJECTED') {
            return {
                label: 'Reprovada',
                colorHsl: [6, 84, 75],
                textColor: '#d65a50',
                bgColor: '#fde8e8',
                icon: 'cancel',
            };
        } else {
            return {
                label: 'Pendente',
                colorHsl: [197, 43, 61],
                textColor: '#3d7a92',
                bgColor: '#e1f5fe',
                icon: 'pending',
            };
        }
    };

    const enabledAnswers = protocolWAnswers
        ? protocolWAnswers.applications.flatMap((application) => application.answers.filter((answer) => answer.enabled === true))
        : [];

    if (error) return <ErrorPage text={error.text} description={error.description} />;

    if (isLoading) return <SplashPage text="Carregando respostas do protocolo..." />;

    return (
        <div className="d-flex flex-column vh-100 overflow-hidden">
            <div className="row align-items-stretch h-100 g-0">
                <div className="col-auto bg-coral-red d-flex position-lg-sticky top-0">
                    <div className="offcanvas-lg offcanvas-start bg-coral-red d-flex w-auto" tabIndex="-1" id="sidebar">
                        <Sidebar showExitButton={false} />
                    </div>
                </div>
                <div className="col d-flex flex-column h-100">
                    <NavBar showNavTogglerMobile={true} showNavTogglerDesktop={false} />
                    <CustomContainer className="font-barlow flex-grow-1 overflow-y-scroll p-4" df="12" md="10">
                        <div className="row align-items-center gx-2 gy-3 mb-4">
                            <div className="col-12 col-md-9 order-1 d-flex flex-column align-items-center align-items-md-start mb-2 mb-md-0">
                                <div className="d-flex align-items-center">
                                    <span className="color-gray">{`Protocolos`}</span>
                                    <MaterialSymbol icon="chevron_right" size={24} fill={1} grade={200} weight={400} className="mx-2" />
                                    <Link className="color-dark-gray" to={`/dash/protocols/${protocolId}`}>
                                        {protocolWAnswers.title}
                                    </Link>
                                </div>
                                <h4 className="fw-bold color-dark-gray">Gestão de Respostas</h4>
                            </div>
                            {/* <div className="col order-1 order-md-2">
                                <TextButton text={'Exportar'} hsl={[197, 43, 61]} onClick={() => {}} disabled={true} />
                            </div> */}
                        </div>
                        <div className="bg-light-gray rounded-4 mb-3 p-3">
                            <div className="row gx-2 justify-content-between align-items-center">
                                <div className="col">
                                    <h2 className="color-dark-gray fw-medium fs-5 m-0">
                                        Aplicações ({protocolWAnswers.applications.length}) e respostas ({enabledAnswers.length})
                                    </h2>
                                    <p className="color-dark-gray fw-medium fs-6 m-0">
                                        Clique em uma aplicação, questão ou resposta para filtrar{' '}
                                        {(selectedAnswer !== undefined ||
                                            selectedItem !== undefined ||
                                            selectedApplication !== undefined) && (
                                            <a
                                                href="#answerTab"
                                                onClick={() => setVisualization(undefined, undefined, undefined)}
                                                className="color-dark-gray fw-bold fs-6"
                                            >
                                                (limpar filtros)
                                            </a>
                                        )}
                                    </p>
                                </div>
                                <div className="col-auto">
                                    <RoundedButton
                                        hsl={[0, 0, 85]}
                                        size={32}
                                        icon="expand_content"
                                        dataBsToggle="collapse"
                                        dataBsTarget="#applicationCollapse"
                                        aria-controls="applicationCollapse"
                                        className="color-dark-gray"
                                    />
                                </div>
                            </div>
                            <div className="collapse" id="applicationCollapse">
                                {protocolWAnswers.applications.map((application) => {
                                    return (
                                        <div key={'answer-' + application.id} className="bg-white rounded-4 mt-3 p-3">
                                            <div className="row gx-2 justify-content-between align-items-center">
                                                <div className="col-10">
                                                    <a
                                                        className="color-dark-gray fw-bold fs-6"
                                                        href="#answerTab"
                                                        onClick={() => setVisualization(undefined, undefined, application.id)}
                                                    >
                                                        Aplicação #{application.id} -{' '}
                                                        {application.applier.username +
                                                            ' - ' +
                                                            new Date(application.createdAt).toLocaleDateString()}
                                                    </a>
                                                </div>
                                                <div className="col-auto">
                                                    <RoundedButton
                                                        hsl={[0, 0, 100]}
                                                        size={20}
                                                        icon="expand_content"
                                                        dataBsToggle="collapse"
                                                        dataBsTarget={'#answerCollapse' + application.id}
                                                        aria-controls={'answerCollapse' + application.id}
                                                        className="color-dark-gray"
                                                    />
                                                </div>
                                            </div>
                                            <div className="collapse" id={'answerCollapse' + application.id}>
                                                {application.answers
                                                    .filter((answer) => answer.enabled === true)
                                                    .map((answer) => {
                                                        const status = getStatusConfig(answer.approvalStatus);
                                                        return (
                                                            <div key={'answer-' + answer.id} className="mt-1">
                                                                <div className="row gx-2 justify-content-between align-items-center">
                                                                    <div className="col-10">
                                                                        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-start">
                                                                            <a
                                                                                className="color-dark-gray fs-6"
                                                                                href="#answerTab"
                                                                                onClick={() =>
                                                                                    setVisualization(answer.id, undefined, undefined)
                                                                                }
                                                                            >
                                                                                Resposta #{answer.id} -{' '}
                                                                                {answer.user.username +
                                                                                    ' - ' +
                                                                                    new Date(answer.date).toLocaleDateString()}
                                                                            </a>
                                                                            {application.actions.toApproveAnswers === true && (
                                                                                <span
                                                                                    className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-bold"
                                                                                    style={{
                                                                                        backgroundColor: status.bgColor,
                                                                                        color: status.textColor,
                                                                                        fontSize: '0.65rem',
                                                                                        textTransform: 'uppercase',
                                                                                        letterSpacing: '0.5px',
                                                                                    }}
                                                                                >
                                                                                    <MaterialSymbol icon={status.icon} size={14} fill={1} />
                                                                                    {status.label}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    {application.actions.toApproveAnswers === true && (
                                                                        <div className="col-auto d-flex align-items-center gap-3 mt-1">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <RoundedButton
                                                                                    hsl={
                                                                                        answer.approvalStatus === 'APPROVED'
                                                                                            ? [97, 43, 70]
                                                                                            : [0, 0, 85]
                                                                                    }
                                                                                    className="text-white"
                                                                                    size={26}
                                                                                    onClick={() =>
                                                                                        answer.approvalStatus !== 'APPROVED' &&
                                                                                        toggleApprovalStatus(answer.id, 'APPROVED')
                                                                                    }
                                                                                    icon="check"
                                                                                />
                                                                                <RoundedButton
                                                                                    hsl={
                                                                                        answer.approvalStatus === 'REJECTED'
                                                                                            ? [6, 84, 75]
                                                                                            : [0, 0, 85]
                                                                                    }
                                                                                    className="text-white"
                                                                                    size={26}
                                                                                    onClick={() =>
                                                                                        answer.approvalStatus !== 'REJECTED' &&
                                                                                        toggleApprovalStatus(answer.id, 'REJECTED')
                                                                                    }
                                                                                    icon="close"
                                                                                />
                                                                            </div>
                                                                            <div className="d-flex align-items-center ms-auto">
                                                                                <RoundedButton
                                                                                    hsl={[355, 78, 66]}
                                                                                    className="text-white"
                                                                                    size={26}
                                                                                    onClick={() => toggleEnablement(answer.id)}
                                                                                    icon="delete"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {protocolWAnswers.applications.some((app) => app.keepLocation) && (
                            <div className="bg-light-gray rounded-4 mb-3 p-3">
                                <div className="row gx-2 justify-content-between align-items-center">
                                    <div className="col">
                                        <div className="col">
                                            <h2 className="color-dark-gray fw-medium fs-5 m-0">Localizações</h2>
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <RoundedButton
                                            hsl={[0, 0, 85]}
                                            size={32}
                                            icon="expand_content"
                                            dataBsToggle="collapse"
                                            dataBsTarget="#locationCollapse"
                                            aria-controls="locationCollapse"
                                            className="color-dark-gray"
                                        />
                                    </div>
                                </div>

                                <div className="collapse show" id="locationCollapse">
                                    <div className="rounded-4 overflow-hidden bg-white mt-3">
                                        <MapContainer center={[-14.235, -51.9253]} zoom={3} style={{ height: '400px' }} ref={mapRef}>
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            {protocolWAnswers.applications
                                                .filter(
                                                    (application) =>
                                                        selectedApplication === undefined || application.id === selectedApplication
                                                )
                                                .flatMap((application) => application.answers)
                                                .filter(
                                                    (answer) =>
                                                        (selectedAnswer === undefined || answer.id === selectedAnswer) &&
                                                        answer.enabled === true &&
                                                        answer.coordinate
                                                )
                                                .map((answer) => {
                                                    return (
                                                        <Marker
                                                            position={[answer.coordinate.latitude, answer.coordinate.longitude]}
                                                            key={'marker-' + answer.id}
                                                            eventHandlers={{
                                                                add: () =>
                                                                    mapRef.current.setView([
                                                                        answer.coordinate.latitude,
                                                                        answer.coordinate.longitude,
                                                                    ]),
                                                            }}
                                                        >
                                                            <Popup>
                                                                <a
                                                                    className="color-dark-gray fw-bold"
                                                                    href="#answerTab"
                                                                    onClick={() => setVisualization(answer.id, undefined, undefined)}
                                                                >
                                                                    {answer.user.username +
                                                                        ' - ' +
                                                                        new Date(answer.date).toLocaleDateString()}
                                                                </a>
                                                            </Popup>
                                                        </Marker>
                                                    );
                                                })}
                                        </MapContainer>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="row align-items-center gx-2 gy-3" id="answerTab">
                            <div className="col">
                                <h4 className="fw-bold color-dark-gray">Respostas</h4>
                            </div>
                        </div>
                        <div className=" mb-lg-4">
                            {protocolWAnswers.pages.map((page, pageIndex) => {
                                return page.itemGroups.map((itemGroup, itemGroupIndex) => {
                                    return (() => {
                                        switch (itemGroup.type) {
                                            case 'ONE_DIMENSIONAL':
                                                return itemGroup.items.map((item) => {
                                                    const sortedAnswers =
                                                        protocolWAnswers.applications
                                                            ?.flatMap((app) => app.answers || [])
                                                            .sort((a, b) => a.id - b.id) || [];

                                                    const enabledAnswersEntries = Object.entries(item.itemAnswers).filter(
                                                        ([applicationAnswerId]) => {
                                                            const meta = sortedAnswers.find(
                                                                (a) => a.id === Number(applicationAnswerId) + 1
                                                            );
                                                            return meta?.enabled === true;
                                                        }
                                                    );
                                                    return (
                                                        (selectedItem === undefined || selectedItem === item.id) && (
                                                            <div key={'input-' + item.id} className="bg-light-gray rounded-4 mb-3 p-3">
                                                                <div className="row gx-2 justify-content-between align-items-center">
                                                                    <div className="col-10">
                                                                        <a
                                                                            href="#answerTab"
                                                                            onClick={() => setVisualization(undefined, item.id, undefined)}
                                                                            className="d-block color-dark-gray fw-medium fs-5 m-0"
                                                                        >
                                                                            {item.text}
                                                                        </a>
                                                                    </div>
                                                                    <div className="col-auto">
                                                                        <RoundedButton
                                                                            hsl={[0, 0, 85]}
                                                                            size={32}
                                                                            icon="expand_content"
                                                                            dataBsToggle="collapse"
                                                                            dataBsTarget={'#itemCollapse' + item.id}
                                                                            aria-controls={'itemCollapse' + item.id}
                                                                            className="color-dark-gray"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="collapse show" id={'itemCollapse' + item.id}>
                                                                    {enabledAnswersEntries.map(([itemAnswerId, itemAnswer]) => {
                                                                        return (
                                                                            (selectedAnswer === undefined ||
                                                                                itemAnswer.group?.applicationAnswer?.id ===
                                                                                    selectedAnswer) &&
                                                                            (selectedApplication === undefined ||
                                                                                protocolWAnswers.applications
                                                                                    .find(
                                                                                        (application) =>
                                                                                            application.id === selectedApplication
                                                                                    )
                                                                                    .answers.find(
                                                                                        (answer) =>
                                                                                            answer.id ===
                                                                                            itemAnswer.group?.applicationAnswer?.id
                                                                                    ) !== undefined) && (
                                                                                <div
                                                                                    key={'answer-' + itemAnswer.id}
                                                                                    className="bg-white rounded-4 mt-3 p-2 px-3"
                                                                                >
                                                                                    <p className="fw-semibold fs-6 color-dark-gray m-0 mb-1">
                                                                                        {protocolWAnswers.applications.flatMap(
                                                                                            (application) =>
                                                                                                application.answers.map((answer) => {
                                                                                                    return answer.id ===
                                                                                                        itemAnswer.group?.applicationAnswer
                                                                                                            ?.id
                                                                                                        ? answer.user.username +
                                                                                                              ' - ' +
                                                                                                              new Date(
                                                                                                                  answer.date
                                                                                                              ).toLocaleDateString()
                                                                                                        : '';
                                                                                                })
                                                                                        )}
                                                                                    </p>
                                                                                    {item.type === 'UPLOAD' ? (
                                                                                        <Gallery
                                                                                            className="mt-3"
                                                                                            item={{
                                                                                                files: itemAnswer.files.map((file) => ({
                                                                                                    path: file.path,
                                                                                                })),
                                                                                            }}
                                                                                            galleryModalRef={galleryModalRef}
                                                                                        />
                                                                                    ) : (
                                                                                        <p className="fw-medium fs-6 color-dark-gray m-0">
                                                                                            {itemAnswer.text}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            )
                                                                        );
                                                                    })}
                                                                    {item.itemAnswers.length === 0 && item.itemOptions.length === 0 && (
                                                                        <div className="bg-white rounded-4 mt-3 p-2 px-3">
                                                                            <p className="fw-medium fs-6 color-dark-gray m-0">
                                                                                0 respostas
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                    {item.itemOptions.map((itemOption) => {
                                                                        return (
                                                                            <div
                                                                                key={'answer-' + itemOption.id}
                                                                                className="bg-white rounded-4 mt-3 p-2 px-3"
                                                                            >
                                                                                <p className="fw-semibold fs-6 color-dark-gray m-0 mb-1">
                                                                                    {itemOption.text +
                                                                                        ' - ' +
                                                                                        itemOption.optionAnswers.filter(
                                                                                            (optionAnswer) =>
                                                                                                (selectedAnswer === undefined ||
                                                                                                    optionAnswer.group?.applicationAnswer
                                                                                                        ?.id === selectedAnswer) &&
                                                                                                (selectedApplication === undefined ||
                                                                                                    protocolWAnswers.applications
                                                                                                        .find(
                                                                                                            (application) =>
                                                                                                                application.id ===
                                                                                                                selectedApplication
                                                                                                        )
                                                                                                        .answers.find(
                                                                                                            (answer) =>
                                                                                                                answer.id ===
                                                                                                                optionAnswer.group
                                                                                                                    ?.applicationAnswer?.id
                                                                                                        ) !== undefined)
                                                                                        ).length +
                                                                                        ' respostas'}
                                                                                </p>
                                                                                {itemOption.optionAnswers.map((optionAnswer) => {
                                                                                    return (
                                                                                        (selectedAnswer === undefined ||
                                                                                            optionAnswer.group?.applicationAnswer?.id ===
                                                                                                selectedAnswer) &&
                                                                                        (selectedApplication === undefined ||
                                                                                            protocolWAnswers.applications
                                                                                                .find(
                                                                                                    (application) =>
                                                                                                        application.id ===
                                                                                                        selectedApplication
                                                                                                )
                                                                                                .answers.find(
                                                                                                    (answer) =>
                                                                                                        answer.id ===
                                                                                                        optionAnswer.group
                                                                                                            ?.applicationAnswer?.id
                                                                                                ) !== undefined) && (
                                                                                            <p
                                                                                                className="fw-semibold fs-6 color-dark-gray m-0"
                                                                                                key={'option-answer-' + optionAnswer.id}
                                                                                            >
                                                                                                {protocolWAnswers.applications.flatMap(
                                                                                                    (application) =>
                                                                                                        application.answers.map(
                                                                                                            (answer) => {
                                                                                                                return answer.id ===
                                                                                                                    optionAnswer.group
                                                                                                                        ?.applicationAnswer
                                                                                                                        ?.id
                                                                                                                    ? answer.user.username +
                                                                                                                          ' - ' +
                                                                                                                          new Date(
                                                                                                                              answer.date
                                                                                                                          ).toLocaleDateString()
                                                                                                                    : '';
                                                                                                            }
                                                                                                        )
                                                                                                )}
                                                                                            </p>
                                                                                        )
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {item.itemOptions
                                                                        .flatMap((itemOption) => itemOption.optionAnswers)
                                                                        .filter(
                                                                            (optionAnswer) =>
                                                                                (selectedAnswer === undefined ||
                                                                                    optionAnswer.group?.applicationAnswer?.id ===
                                                                                        selectedAnswer) &&
                                                                                (selectedApplication === undefined ||
                                                                                    protocolWAnswers.applications
                                                                                        .find(
                                                                                            (application) =>
                                                                                                application.id === selectedApplication
                                                                                        )
                                                                                        .answers.find(
                                                                                            (answer) =>
                                                                                                answer.id ===
                                                                                                optionAnswer.group?.applicationAnswer?.id
                                                                                        ) !== undefined)
                                                                        ).length > 0 && (
                                                                        <div className="rounded-4 overflow-hidden mt-3">
                                                                            <Chart
                                                                                chartType="PieChart"
                                                                                data={[['Resposta', 'Quantidade']].concat(
                                                                                    item.itemOptions.map((itemOption) => {
                                                                                        return [
                                                                                            itemOption.text,
                                                                                            itemOption.optionAnswers.filter(
                                                                                                (optionAnswer) =>
                                                                                                    (selectedAnswer === undefined ||
                                                                                                        optionAnswer.group
                                                                                                            ?.applicationAnswer?.id ===
                                                                                                            selectedAnswer) &&
                                                                                                    (selectedApplication === undefined ||
                                                                                                        protocolWAnswers.applications
                                                                                                            .find(
                                                                                                                (application) =>
                                                                                                                    application.id ===
                                                                                                                    selectedApplication
                                                                                                            )
                                                                                                            .answers.find(
                                                                                                                (answer) =>
                                                                                                                    answer.id ===
                                                                                                                    optionAnswer.group
                                                                                                                        ?.applicationAnswer
                                                                                                                        ?.id
                                                                                                            ) !== undefined)
                                                                                            ).length,
                                                                                        ];
                                                                                    })
                                                                                )}
                                                                                options={{
                                                                                    chartArea: { top: 8, height: '90%', width: '90%' },
                                                                                    legend: { position: 'bottom' },
                                                                                    colors: [
                                                                                        '#F59489',
                                                                                        '#91CAD6',
                                                                                        '#FECF86',
                                                                                        '#4E9BB9',
                                                                                        '#EC6571',
                                                                                        '#AAD390',
                                                                                        '#8C6A80',
                                                                                        '#70A6A6',
                                                                                        '#FACD63',
                                                                                        '#578AA2',
                                                                                        '#E64E5E',
                                                                                        '#91BD7E',
                                                                                        '#F9A98F',
                                                                                        '#76C4D1',
                                                                                        '#FEDB8A',
                                                                                        '#5C97B2',
                                                                                        '#D85A6A',
                                                                                        '#89A86B',
                                                                                        '#F7BC92',
                                                                                        '#6FACB5',
                                                                                        '#E14953',
                                                                                        '#A1C588',
                                                                                        '#F5D39A',
                                                                                        '#568BA5',
                                                                                        '#C44D59',
                                                                                        '#7FA569',
                                                                                        '#E6A17C',
                                                                                        '#619BB0',
                                                                                        '#F39C9F',
                                                                                        '#ADC192',
                                                                                        '#FFD07D',
                                                                                        '#5499AE',
                                                                                        '#E6606F',
                                                                                        '#97BD83',
                                                                                        '#F6C2A3',
                                                                                        '#6BA3B8',
                                                                                        '#E15661',
                                                                                        '#90B67A',
                                                                                        '#F7AE9C',
                                                                                        '#75BFCB',
                                                                                        '#E6737D',
                                                                                        '#A2CA93',
                                                                                        '#FAD4AC',
                                                                                        '#639FB7',
                                                                                        '#D45B66',
                                                                                        '#83AB77',
                                                                                        '#F9B69B',
                                                                                        '#71B4C6',
                                                                                        '#E75762',
                                                                                        '#A8C599',
                                                                                    ],
                                                                                    is3D: true,
                                                                                }}
                                                                                height={'400px'}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    );
                                                });
                                            case 'TEXTBOX_TABLE':
                                            case 'RADIO_TABLE':
                                            case 'CHECKBOX_TABLE':
                                                const hasAnswers = protocolWAnswers.applications.some(
                                                    (app) =>
                                                        (selectedApplication === undefined || app.id === selectedApplication) &&
                                                        app.answers.some(
                                                            (ans) =>
                                                                (selectedAnswer === undefined || ans.id === selectedAnswer) && ans.enabled
                                                        )
                                                );

                                                if (!hasAnswers) return null;

                                                return (
                                                    <div key={'table-group-' + itemGroup.id} className="bg-light-gray rounded-4 mb-3 p-3">
                                                        {/* Cabeçalho do Grupo (Igual ao One Dimensional) */}
                                                        <div className="row gx-2 justify-content-between align-items-center">
                                                            <div className="col-10 d-flex align-items-center flex-wrap">
                                                                <span className="color-gray fw-medium">{`Protocolos`}</span>
                                                                <MaterialSymbol
                                                                    icon="chevron_right"
                                                                    size={18}
                                                                    fill={1}
                                                                    grade={200}
                                                                    weight={400}
                                                                    className="mx-1"
                                                                />
                                                                <span className="color-gray fw-medium">{`Página ${pageIndex + 1}`}</span>
                                                                <MaterialSymbol
                                                                    icon="chevron_right"
                                                                    size={18}
                                                                    fill={1}
                                                                    grade={200}
                                                                    weight={400}
                                                                    className="mx-1"
                                                                />
                                                                <span className="color-gray fw-medium">{`Grupo ${
                                                                    itemGroupIndex + 1
                                                                }`}</span>
                                                                <MaterialSymbol
                                                                    icon="chevron_right"
                                                                    size={18}
                                                                    fill={1}
                                                                    grade={200}
                                                                    weight={400}
                                                                    className="mx-1"
                                                                />
                                                                <h2 className="color-dark-gray fw-medium fs-5 m-0">
                                                                    {/* Aqui vai o nome da tabela + sufixo se necessário */}
                                                                    {itemGroup.type === 'TEXTBOX_TABLE' && 'Tabela de texto'}
                                                                    {itemGroup.type === 'RADIO_TABLE' && 'Tabela de escolha simples'}
                                                                    {itemGroup.type === 'CHECKBOX_TABLE' && 'Tabela de múltipla escolha'}
                                                                </h2>
                                                            </div>
                                                            <div className="col-auto">
                                                                <RoundedButton
                                                                    hsl={[0, 0, 85]}
                                                                    size={32}
                                                                    icon="expand_content"
                                                                    dataBsToggle="collapse"
                                                                    dataBsTarget={'#tableCollapse' + itemGroup.id}
                                                                    aria-controls={'tableCollapse' + itemGroup.id}
                                                                    className="color-dark-gray"
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Conteúdo Colapsável */}
                                                        <div className="collapse show" id={'tableCollapse' + itemGroup.id}>
                                                            {protocolWAnswers.applications
                                                                .filter(
                                                                    (app) =>
                                                                        selectedApplication === undefined || app.id === selectedApplication
                                                                )
                                                                .flatMap((app) => app.answers)
                                                                .filter(
                                                                    (ans) =>
                                                                        (selectedAnswer === undefined || ans.id === selectedAnswer) &&
                                                                        ans.enabled
                                                                )
                                                                .map((answer) => (
                                                                    <div
                                                                        key={`table-wrapper-${itemGroup.id}-${answer.id}`}
                                                                        className="mt-3"
                                                                    >
                                                                        {/* Identificador da Resposta (Opcional: exibe quem respondeu e a data) */}
                                                                        <div className="d-flex pb-1">
                                                                            <MaterialSymbol
                                                                                icon="person_edit"
                                                                                size={24}
                                                                                color="#535353"
                                                                                fill={0}
                                                                            />
                                                                            <p className="fw-semibold fs-6 color-dark-gray ps-2 m-0">
                                                                                {answer.user.username} -{' '}
                                                                                {new Date(answer.date).toLocaleDateString()}
                                                                            </p>
                                                                        </div>

                                                                        <TableInput
                                                                            isProtocol={true}
                                                                            applicationAnswerId={answer.id}
                                                                            group={itemGroup}
                                                                            answersPage={true}
                                                                            disabled={true}
                                                                            pageIndex={pageIndex + 1}
                                                                            groupIndex={itemGroupIndex + 1}
                                                                            answer={answer}
                                                                        />
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                );
                                            default:
                                                return null;
                                        }
                                    })();
                                });
                            })}
                        </div>
                    </CustomContainer>
                </div>
            </div>
            <GalleryModal id="ProtocolPageGallery" ref={galleryModalRef} />
            <style>{styles}</style>
        </div>
    );
}

export default ProtocolAnswersPage;
